import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { login, loginWithCertificate } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [certificates, setCertificates] = useState([]);
  const [certLoaded, setCertLoaded] = useState(false);
  const [selectedCert, setSelectedCert] = useState("");

  const loadCertificates = async () => {
    try {
      const { data } = await api.get("/auth/certificates");
      setCertificates(data);
      setCertLoaded(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Вход выполнен успешно");
      navigate("/");
    } catch (e) {
      try {
        await api.post("/platform-admin/login", { email, password });
        toast.success("Вход выполнен успешно");
        navigate("/platform-admin");
      } catch {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCertLogin = async () => {
    if (!selectedCert) return;
    setError("");
    setLoading(true);
    try {
      await loginWithCertificate(selectedCert);
      toast.success("Вход по сертификату выполнен");
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden bg-white border-r border-slate-200">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:22px_22px]" />
        <div className="absolute -z-10 top-10 left-1/4 w-72 h-72 rounded-full bg-[#4E9BEE]/10 blur-3xl" />
        <div className="absolute -z-10 bottom-10 right-1/4 w-72 h-72 rounded-full bg-[#10B981]/10 blur-3xl" />
        <Link to="/" className="relative z-10 flex items-center gap-3" data-testid="login-logo-link">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] flex items-center justify-center shadow-md shadow-blue-200">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-slate-900 font-display">КриптоДок</span>
        </Link>
        <div className="relative z-10 space-y-6 max-w-md">
          <h1 className="text-4xl font-bold text-slate-900 leading-tight font-display">
            Документооборот без бумаги и курьеров
          </h1>
          <p className="text-slate-600 text-base">
            Принимайте, отправляйте и подписывайте документы электронной подписью в едином личном кабинете.
          </p>
          <div className="space-y-3 pt-4">
            <div className="flex items-center gap-3 text-slate-700 text-sm">
              <FileSignature className="w-4 h-4 text-[#2563EB] shrink-0" />
              Визуальная печать ЭЦП для каждого статуса документа
            </div>
            <div className="flex items-center gap-3 text-slate-700 text-sm">
              <ShieldCheck className="w-4 h-4 text-[#2563EB] shrink-0" />
              Вход по сертификату, email или реквизитам компании
            </div>
          </div>
        </div>
        <div className="relative z-10 text-slate-400 text-xs">© 2026 КриптоДок</div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6" data-testid="login-logo-link-mobile">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-display">КриптоДок</span>
          </Link>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">Вход в личный кабинет</h2>
          <p className="text-sm text-slate-500 mb-6">Войдите, чтобы продолжить работу с документами</p>

          <Tabs
            defaultValue="password"
            onValueChange={(v) => {
              setError("");
              if (v === "certificate" && !certLoaded) loadCertificates();
            }}
          >
            <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="login-tabs">
              <TabsTrigger value="password" data-testid={LOGIN.tabPassword}>
                Email и пароль
              </TabsTrigger>
              <TabsTrigger value="certificate" data-testid={LOGIN.tabCertificate}>
                По сертификату ЭЦП
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={LOGIN.emailInput}
                    placeholder="you@company.ru"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Пароль</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={LOGIN.passwordInput}
                    placeholder="••••••••"
                  />
                </div>
                {error && (
                  <p className="text-sm text-rose-600" data-testid="login-error">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full text-white bg-gradient-to-r from-[#4E9BEE] to-[#2563EB] hover:scale-[1.01] active:scale-[0.99] transition-transform shadow-md shadow-blue-200"
                  disabled={loading}
                  data-testid={LOGIN.submitButton}
                >
                  {loading ? "Входим…" : "Войти"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="certificate">
              <div className="space-y-4">
                <p className="text-xs text-slate-500">
                  Выберите сертификат усиленной квалифицированной электронной подписи (КЭП), привязанный к вашей организации.
                </p>
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="certificate-selector-dropdown">
                  {certificates.map((c) => (
                    <button
                      key={c.certificate_id}
                      type="button"
                      onClick={() => setSelectedCert(c.certificate_id)}
                      data-testid={`certificate-option-${c.certificate_id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedCert === c.certificate_id ? "border-[#4E9BEE] bg-[#4E9BEE]/10" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="text-sm font-medium text-slate-900">{c.owner_name}</div>
                      <div className="text-xs text-slate-500">
                        {c.organization_name} · {c.certificate_id}
                      </div>
                      <div className="text-xs text-slate-400">Действителен до {c.valid_until}</div>
                    </button>
                  ))}
                  {certificates.length === 0 && <p className="text-xs text-slate-400">Загрузка сертификатов…</p>}
                </div>
                {error && <p className="text-sm text-rose-600">{error}</p>}
                <Button
                  onClick={handleCertLogin}
                  disabled={!selectedCert || loading}
                  className="w-full text-white bg-gradient-to-r from-[#4E9BEE] to-[#2563EB] hover:scale-[1.01] active:scale-[0.99] transition-transform shadow-md shadow-blue-200"
                  data-testid={LOGIN.certificateSubmitButton}
                >
                  {loading ? "Входим…" : "Войти по сертификату"}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Нет организации в системе?{" "}
            <Link to="/register" className="text-[#2563EB] font-medium hover:underline" data-testid={LOGIN.registerLink}>
              Зарегистрировать компанию
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
